import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { DeleteEnvironmentDialog } from '@/components/DeleteEnvironmentDialog';
import { useReadOnlyMode } from '@nimbitlabs/features';
import { PermissionGate } from '@nimbitlabs/features';
import type { Environment } from '@/types/environment';
import { Alert, AlertDescription, AlertTitle, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Separator, ErrorAlert } from '@nimbitlabs/ui';
import { 
  Search, 
  Edit, 
  Trash2, 
  Server, 
  Activity, 
  Clock,
  Globe,
  Lock,
  Info,
  Layers,
  Plus
} from 'lucide-react';

export const Environments = () => {
  const navigate = useNavigate();
  const { environments, loading, deleteEnvironment: deleteEnvironmentFn } = useEnvironmentsStore();
  const { isReadOnly } = useReadOnlyMode();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingEnvironment, setDeletingEnvironment] = useState<Environment | null>(null);
  const [error, setError] = useState<unknown>(null);
  
  const deleteEnvironment = async (id: string) => {
    await deleteEnvironmentFn(id);
  };

  const getTypeOrder = (type: string) => {
    const typeMap: Record<string, number> = {
      'Development': 1,
      'Uat': 2,
      'Staging': 2,
      'Production': 3,
    };
    return typeMap[type] || 999;
  };

  const filteredEnvironments = environments
    .filter(env =>
      env.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      env.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      env.type.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      // Sort unlocked first, then locked
      if (a.isLocked !== b.isLocked) {
        return a.isLocked ? 1 : -1;
      }
      // Then sort by environment type: Development, UAT, Production
      return getTypeOrder(a.type) - getTypeOrder(b.type);
    });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-500/20 text-green-600 border-green-500/40 dark:text-green-400';
      case 'Inactive':
        return 'bg-muted/30 text-muted-foreground border-muted/40';
      case 'Maintenance':
        return 'bg-orange-500/20 text-orange-600 border-orange-500/40 dark:text-orange-400';
      default:
        return 'bg-muted/30 text-muted-foreground border-muted/40';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Production':
        return <Layers className="h-6 w-6 text-primary" />;
      case 'Development':
        return <Layers className="h-6 w-6 text-blue-500" />;
      case 'Uat':
      case 'Staging':
        return <Layers className="h-6 w-6 text-orange-500" />;
      default:
        return <Layers className="h-6 w-6 text-muted-foreground" />;
    }
  };

  const handleDelete = async () => {
    if (!deletingEnvironment) return;
    
    try {
      setError(null);
      await deleteEnvironment(deletingEnvironment.id);
    } catch (err) {
      setError(err);
    } finally {
      setDeletingEnvironment(null);
    }
  };

  if (loading && environments.length === 0) {
    return (
      <div className="space-y-3 md:space-y-6 px-2 md:px-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Environments</h1>
            <p className="text-sm md:text-base text-muted-foreground">Manage your application environments</p>
          </div>
        </div>
        <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-muted rounded w-3/4"></div>
                <div className="h-4 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded"></div>
                  <div className="h-4 bg-muted rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-6 px-2 md:px-0">
      <ErrorAlert error={error} />
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Environments</h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Manage your application deployment environments
          </p>
        </div>
        <PermissionGate permission="environments:create">
          <Button onClick={() => navigate('/environments/new')} className="w-fit">
            <Plus className="h-4 w-4 mr-2" />
            Add Environment
          </Button>
        </PermissionGate>
      </div>

      {/* Locked Resources Alert */}
      {environments.some(e => e.isLocked) && (
        <Alert className="border border-warning/15 bg-warning/5">
          <Info className="h-4 w-4 text-warning" />
          <AlertTitle className="text-warning">Some environments are locked</AlertTitle>
          <AlertDescription>
            {environments.filter(e => e.isLocked).length} environment(s) are locked due to subscription limits. 
            Your data is safe. Delete environments to free up quota or upgrade your plan to unlock them.
          </AlertDescription>
        </Alert>
      )}

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search environments..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Environments Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredEnvironments.map((environment, index) => (
          <Card 
            key={environment.id} 
            className={`group relative overflow-hidden border-border/40 bg-card/50 hover:shadow-lg hover:border-primary/30 hover:bg-card transition-all duration-300 animate-fade-in cursor-pointer ${
              environment.isLocked ? 'opacity-60' : ''
            }`}
            style={{ animationDelay: `${index * 50}ms` }}
            onClick={() => !environment.isLocked && navigate(`/environments/${environment.id}`)}
          >
            <CardHeader className="pb-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-lg bg-primary/5 border border-primary/10">
                  {getTypeIcon(environment.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <CardTitle className="text-xl font-bold truncate">{environment.name}</CardTitle>
                    
                    {/* Status Badge */}
                    <Badge 
                      variant="outline" 
                      className={getStatusColor(environment.status)}
                    >
                      <span className="capitalize">{environment.status}</span>
                    </Badge>

                    {environment.isLocked && (
                      <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 flex-shrink-0">
                        <Lock className="h-3 w-3 mr-1" />
                        Locked
                      </Badge>
                    )}
                  </div>
                  <CardDescription className="line-clamp-2 text-sm">
                    {environment.description}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 pt-0">
              {/* Type Badge */}
              <div className="flex items-center justify-between py-3 border-y border-border/40">
                <div className="flex items-center gap-1.5">
                  <Server className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Environment Type
                  </span>
                </div>
                <Badge variant="outline" className="bg-info/10 text-info border-info/30 font-semibold capitalize">
                  {environment.type}
                </Badge>
              </div>

              {/* Metadata */}
              <div className="space-y-2">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Timeline
                </span>
                <div className="text-xs text-muted-foreground space-y-1">
                  <div className="flex items-center gap-2">
                    <Clock className="h-3 w-3" />
                    <span>Created: {new Date(environment.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-3 w-3" />
                    <span>Updated: {new Date(environment.updatedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-2">
                <div className="flex gap-1.5">
                  <PermissionGate permission="environments:update">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={environment.isLocked}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/environments/${environment.id}`);
                      }}
                      className="h-9 w-9 p-0 rounded-lg hover:bg-blue-500/10 hover:text-blue-500 transition-all group/btn"
                    >
                      <Edit className="h-4 w-4 group-hover/btn:rotate-12 transition-transform" />
                    </Button>
                  </PermissionGate>
                  <PermissionGate permission="environments:delete">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingEnvironment(environment);
                      }}
                      className="h-9 w-9 p-0 rounded-lg hover:bg-destructive/10 hover:text-destructive transition-all group/btn"
                    >
                      <Trash2 className="h-4 w-4 group-hover/btn:rotate-12 transition-transform" />
                    </Button>
                  </PermissionGate>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  disabled={environment.isLocked}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/environments/${environment.id}`);
                  }}
                  className="font-medium bg-blue-500/10 text-blue-600 border-blue-500/30 hover:bg-blue-500/20 hover:border-blue-500/40 transition-all"
                >
                  View Details
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredEnvironments.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Server className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No environments found</h3>
            <p className="text-muted-foreground">
              {searchQuery ? 'Try adjusting your search criteria.' : 'Get started by creating your first environment using the button above.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Delete Dialog */}
      <DeleteEnvironmentDialog
        environment={deletingEnvironment}
        open={!!deletingEnvironment}
        onOpenChange={(open) => !open && setDeletingEnvironment(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
};
